/**
 * The looks a shop can wear.
 *
 * A shop picks one when it is created, and both apps paint it for everyone who belongs to that
 * shop. The point is recognition, not decoration: a manager at Diamond should be able to tell at
 * a glance, from across the counter, that they are not looking at OriTech's till.
 *
 * ## What a theme is allowed to change
 *
 * Two roles, and only two:
 *
 * - `brand` — identity and structure. The rail, the selected nav row, the fallback mark, brand
 *   chips. This is what "the shop's colour" means in this product.
 * - `surface.backdrop` — the space around the POS column, which on a counter laptop is most of
 *   the screen and is therefore the largest single piece of shop identity anywhere in the app.
 *
 * Everything else is off limits, on purpose. `action` stays copper on every theme, because a
 * salesperson learns where the button colour is in their first hour and re-teaching that per
 * shop would cost real mis-taps; status colours stay put because green-means-done is not a brand
 * decision. A theme that could repaint danger would eventually paint a delete button in a shop's
 * house blue.
 *
 * ## Curated, not freeform
 *
 * The owner picks from this list; they cannot type a hex. Every pair here is checked by
 * `scripts/check-contrast.mts` in both light and dark before it can ship, which a colour typed
 * into a form at 9pm is not. Adding a look is one entry in this file plus the same code string
 * in the server's `ShopThemes` — deliberately a code change, so the check runs.
 *
 * ## Logos
 *
 * There is no logo upload and no logo storage. A theme may name a bundled asset — OriTech's own
 * does — and every other shop falls back to a generic mark drawn in `currentColor`, which the
 * apps tint with `--pos-brand-base`. So a shop with no artwork still gets a mark in its own
 * colour rather than somebody else's wordmark.
 */

import { absolute, copper, moss, neutral, plum, sapphire, slate, type Ramp } from '../primitives/color.js';
import { logo } from '../brand.js';
import type { AccentRole } from './roles.js';

export type ShopThemeCode = 'oritech' | 'sapphire' | 'plum' | 'copper' | 'slate' | 'diamond';

/**
 * A full-surface look, for a shop whose brand is not a colour laid over cream but a different
 * ground entirely. Every value is explicit and comes from the client's own brand sheet — nothing
 * here is derived from a ramp, because a ramp derived from a single cyan would be an invention.
 *
 * Roles the sheet did not name are reused from values it did (documented per field), never
 * introduced. Status colours are not overridable at all: success, warning, danger and info keep
 * their meaning across every shop.
 */
export type SurfacePalette = {
  readonly surface: {
    readonly base: string;
    readonly raised: string;
    readonly overlay: string;
    readonly sunken: string;
    readonly backdrop: string;
    /** Tooltips and the reversed callout. */
    readonly inverse: string;
  };
  readonly text: {
    readonly primary: string;
    readonly secondary: string;
    readonly muted: string;
    readonly disabled: string;
    /** On `surface.inverse`. */
    readonly inverse: string;
  };
  readonly border: {
    readonly subtle: string;
    readonly default: string;
    readonly card: string;
    readonly strong: string;
    readonly focus: string;
  };
  readonly brand: AccentRole;
  /**
   * The button colour. On the product's own look this is copper, distinct from moss on purpose;
   * a full-surface theme has one accent and both roles take it — a Diamond page with copper
   * buttons is the product's look leaking through, which is the one thing this type exists to
   * stop.
   */
  readonly action: AccentRole;
  readonly focus: { readonly ring: string; readonly halo: string };
  readonly scrim: string;
  readonly skeleton: string;
  /**
   * The dashboard's main chart and its chrome. The five categorical series are *not* here — they
   * stay identical across every shop so a category is the same colour in both branches — but the
   * value bars, the count line and the gridlines carry no cross-shop meaning and would otherwise
   * be the product's moss and copper on the largest element of the page.
   */
  readonly chart: {
    readonly value: string;
    readonly count: string;
    readonly grid: string;
    readonly axis: string;
    readonly reference: string;
  };
  /**
   * Shadow colour as an `r g b` triple, so the same alphas the base theme uses can be applied
   * over it. The base theme's shadows are warm brown; on a cool ground they read as dirt.
   */
  readonly shadowRgb: string;
};

/**
 * A look, without the code that identifies it. Split out so the neutral look below can be the
 * same shape as a shop's without being assignable where a `ShopThemeCode` is required — it is
 * painted by `clearShopTheme`, never chosen by an owner, and must not reach the picker or the
 * server's `ShopThemes`.
 */
export type ThemeLook = {
  /** Shown in the picker. A colour name, not a shop name — themes outlive the shop that chose one. */
  readonly label: string;
  /** One line under the label, to make two blues distinguishable in a list. */
  readonly description: string;
  /**
   * A bundled asset, or `null` for the generic mark. Never a URL, never uploaded: the fallback
   * is the normal case and the asset is the exception.
   */
  readonly logoMark: string | null;
  /**
   * The colour scheme this look was designed for. Applied when the device has no saved
   * preference of its own — see `applyShopTheme`. A user's own toggle always wins.
   */
  readonly defaultColorScheme: 'light' | 'dark';
  /** The swatch the picker paints, and what the brand role resolves to in light. */
  readonly ramp: Ramp;
  /**
   * Present only for a look that repaints the whole surface, not just the brand role. When set,
   * `ramp` is used for the picker swatch alone and every emitted variable comes from here.
   */
  readonly palette?: { readonly light: SurfacePalette; readonly dark: SurfacePalette };
};

export type ShopTheme = ThemeLook & { readonly code: ShopThemeCode };

/** The value `data-shop-theme` carries when no shop is on screen. Not a `ShopThemeCode`. */
export const NEUTRAL_THEME_CODE = 'neutral';

export type NeutralTheme = ThemeLook & { readonly code: typeof NEUTRAL_THEME_CODE };

/**
 * Diamond's cyan as a ramp, for the picker swatch only. Two anchors are the client's; the rest
 * exist so the type is satisfied, and none of them is emitted as a variable.
 */
const diamondCyan: Ramp = {
  50: '#ECFEFF',
  100: '#CFFAFE',
  200: '#A5F3FC',
  300: '#67E8F9',
  400: '#22D3EE', // client accent
  500: '#06B6D4', // client accent hover
  600: '#0891B2',
  700: '#0E7490',
  800: '#155E75',
  900: '#164E63',
};

/**
 * Diamond, from the client's brand sheet. Dark-first: the values under `dark` are the design;
 * `light` is what the toggle shows if somebody insists.
 *
 * Where the sheet named no value the nearest named one is reused, and each such reuse says so.
 */
const DIAMOND_DARK_ACCENT: AccentRole = {
  base: '#22D3EE', // sheet: accent
  hover: '#06B6D4', // sheet: accent hover
  active: '#06B6D4', // reuse: hover, the sheet has no pressed state
  subtle: '#1A222D', // reuse: surface raised
  subtleText: '#22D3EE', // reuse: accent
  on: '#0A0E14', // reuse: background, for text on a light cyan fill
};

const DIAMOND_DARK: SurfacePalette = {
  surface: {
    base: '#0A0E14', // sheet: dark background
    raised: '#121821', // sheet: dark surface
    overlay: '#1A222D', // sheet: dark surface raised
    sunken: '#0A0E14', // reuse: background, the darkest value the sheet gives
    backdrop: '#0A0E14', // reuse: background
    inverse: '#E8EEF4', // reuse: text primary, the sheet's lightest value
  },
  text: {
    primary: '#E8EEF4', // sheet
    secondary: '#8B99A8', // sheet
    muted: '#8B99A8', // reuse: secondary, the sheet has no third text step
    disabled: '#8B99A8', // reuse; disabled is exempt from AA and is meant to look it
    inverse: '#0A0E14', // reuse: background, on the inverse surface
  },
  border: {
    subtle: '#232D3A', // sheet: dark border
    default: '#232D3A', // reuse
    card: '#232D3A', // reuse
    strong: '#8B99A8', // reuse: text secondary, the only sheet value that clears 3:1 on the ground
    focus: '#22D3EE', // reuse: accent
  },
  brand: DIAMOND_DARK_ACCENT,
  // One accent on the sheet, so buttons and identity share it. Not copper.
  action: DIAMOND_DARK_ACCENT,
  focus: { ring: '#22D3EE', halo: '#0A0E14' }, // reuse: accent over background
  scrim: 'rgb(10 14 20 / 0.62)', // reuse: background at the base theme's dark alpha
  skeleton: '#1A222D', // reuse: surface raised
  chart: {
    value: '#22D3EE', // reuse: accent
    count: '#8B99A8', // reuse: text secondary — separated from the bars by lightness, not hue
    grid: '#232D3A', // reuse: border
    axis: '#8B99A8', // reuse: text secondary
    reference: '#8B99A8', // reuse
  },
  shadowRgb: '0 0 0', // the base dark theme's own choice: warmth does not survive near-black
};

const DIAMOND_LIGHT_ACCENT: AccentRole = {
  base: '#06B6D4', // reuse: hover is the darker of the two cyans, so it carries on white
  hover: '#22D3EE',
  active: '#22D3EE',
  // Not the page colour: a subtle fill that equals the page is no fill, and the count badge on
  // the cart bar and the initials chip in the header disappeared against it. The sheet's light
  // grey is visible on both the page and a white card.
  subtle: '#E8EEF4', // reuse: the sheet's light grey
  subtleText: '#0A0E14', // reuse: text primary, cyan on white does not reach 4.5:1 as text
  on: '#0A0E14', // reuse: text primary
};

const DIAMOND_LIGHT: SurfacePalette = {
  surface: {
    base: '#F4F7F9', // sheet: light background
    raised: '#FFFFFF', // sheet: light surface
    overlay: '#FFFFFF', // reuse
    sunken: '#F4F7F9', // reuse: background
    backdrop: '#F4F7F9', // reuse: background
    inverse: '#0A0E14', // reuse: text primary, the sheet's darkest value
  },
  text: {
    primary: '#0A0E14', // sheet: light text primary
    // #8B99A8 is the sheet's only secondary and it reads 2.7:1 on the sheet's own light ground,
    // which is not text. The dark border value is the next-darkest thing on the sheet.
    secondary: '#232D3A', // reuse: dark border
    muted: '#232D3A', // reuse
    disabled: '#8B99A8', // reuse; disabled is exempt from AA and is meant to look it
    inverse: '#E8EEF4', // reuse: dark text primary, on the inverse surface
  },
  border: {
    subtle: '#8B99A8', // reuse: text secondary, quiet enough for a hairline on white
    default: '#8B99A8',
    card: '#8B99A8', // reuse: a visible edge on white
    strong: '#232D3A', // reuse: dark border, the only sheet value clearing 3:1 on light
    // Not cyan: neither sheet cyan clears 3:1 on the light ground, and a focus ring that fails
    // 1.4.11 is invisible to the people it exists for. Near-black on light is the standard ring.
    focus: '#0A0E14', // reuse: text primary
  },
  brand: DIAMOND_LIGHT_ACCENT,
  action: DIAMOND_LIGHT_ACCENT,
  focus: { ring: '#0A0E14', halo: '#F4F7F9' }, // reuse: text primary over background
  scrim: 'rgb(10 14 20 / 0.42)', // reuse: text primary at the base theme's light alpha
  skeleton: '#E8EEF4', // reuse: the sheet's light grey (its dark text primary)
  chart: {
    value: '#06B6D4', // reuse: accent (light). Fails 3:1 on the sheet's light ground; waived as brand is.
    count: '#0A0E14', // reuse: text primary — far darker than the bars, so the line reads over them
    grid: '#E8EEF4', // reuse: light grey
    axis: '#232D3A', // reuse: dark border, the light-mode secondary text
    reference: '#8B99A8', // reuse
  },
  shadowRgb: '10 14 20', // reuse: text primary — a cool shadow on a cool ground
};

/*
 * ── The neutral look ──────────────────────────────────────────────────────────
 *
 * What the app wears when no shop is on screen: the POS sign-in on a device that has never
 * signed in, and every device the moment it signs out.
 *
 * It exists because the neutral state previously had nothing underneath it. `clearShopTheme`
 * removed `data-shop-theme` and the page fell through to `:root` — which *is* OriTech, since the
 * base palette is built from the logo's moss and copper. So "belongs to no shop" and "belongs to
 * OriTech" rendered identically, and every other shop's till opened wearing a competitor's green.
 *
 * Full-surface for the reason Diamond is: a look that overrode only `brand` would keep copper
 * buttons, a cream ground and a green-leaning dark shell, all three of which are OriTech's
 * identity rather than neutral chrome. Every non-status role is replaced here.
 *
 * Monochrome, and every value is an existing step of the `neutral` ramp or an `absolute` — no
 * colour is invented. The ramp is warm rather than a true grey, which is a deliberate property of
 * the palette and is the one trace of the house style that remains.
 */

const NEUTRAL_LIGHT_ACCENT: AccentRole = {
  base: neutral[800],
  hover: neutral[900],
  active: absolute.black,
  subtle: neutral[200],
  subtleText: neutral[900],
  on: absolute.white,
};

const NEUTRAL_LIGHT: SurfacePalette = {
  surface: {
    base: neutral[50],
    raised: absolute.white,
    overlay: absolute.white, // reuse: raised — an overlay on white has nowhere lighter to go
    sunken: neutral[200],
    // Not `neutral[100]`: that step is the brand cream, and the backdrop is the largest surface
    // on a counter screen. A grey surround is what makes a signed-out till read as nobody's.
    backdrop: neutral[200],
    inverse: neutral[900],
  },
  text: {
    primary: neutral[900],
    secondary: neutral[700],
    muted: neutral[600],
    disabled: neutral[400], // exempt from AA, and meant to look it
    inverse: neutral[50],
  },
  border: {
    subtle: neutral[200],
    default: neutral[300],
    card: neutral[200], // reuse: subtle
    strong: neutral[500], // the one neutral that clears 3:1 on both themes — see the ramp
    focus: neutral[900],
  },
  brand: NEUTRAL_LIGHT_ACCENT,
  // One accent, carrying both roles. Copper here would be the product's action colour on a
  // screen that belongs to no product.
  action: NEUTRAL_LIGHT_ACCENT,
  focus: { ring: neutral[900], halo: neutral[50] },
  scrim: 'rgb(44 42 37 / 0.42)', // neutral-900 at the base light theme's alpha
  skeleton: neutral[200],
  chart: {
    value: neutral[700],
    count: neutral[900], // far darker than the bars, so the line reads over them
    grid: neutral[200],
    axis: neutral[600],
    reference: neutral[500],
  },
  shadowRgb: '44 42 37', // neutral-900
};

const NEUTRAL_DARK_ACCENT: AccentRole = {
  base: neutral[50],
  hover: absolute.white,
  active: neutral[200],
  subtle: neutral[800],
  subtleText: neutral[200], // not the 100: that step is the brand cream
  on: neutral[900],
};

const NEUTRAL_DARK: SurfacePalette = {
  surface: {
    // Not `ground.dark` or the `surfaceDark` tiers: those lean green so the shell still feels
    // like the logo, which is exactly the association this look exists to drop.
    base: neutral[900],
    raised: neutral[800],
    overlay: neutral[800], // reuse: raised — the ramp has no step between them
    sunken: absolute.black,
    backdrop: absolute.black,
    inverse: neutral[50],
  },
  text: {
    primary: neutral[50],
    secondary: neutral[300],
    muted: neutral[400],
    disabled: neutral[500], // exempt from AA, and meant to look it
    inverse: neutral[900],
  },
  border: {
    subtle: neutral[800],
    default: neutral[700],
    card: neutral[800], // reuse: subtle
    strong: neutral[500],
    focus: neutral[50],
  },
  brand: NEUTRAL_DARK_ACCENT,
  action: NEUTRAL_DARK_ACCENT,
  focus: { ring: neutral[50], halo: neutral[900] },
  scrim: 'rgb(0 0 0 / 0.62)', // the base dark theme's alpha over its own sunken black
  skeleton: neutral[800],
  chart: {
    value: neutral[300],
    // The lightest step, mirroring how light mode gives the count line the darkest: the card it
    // sits on is `neutral[800]`, which is a pale ground for a dark theme, and the mid steps land
    // under 3:1 against it. Separated from the bars by lightness, as every other look does it.
    count: neutral[50],
    grid: neutral[800],
    axis: neutral[300], // reuse: value — the mid steps do not clear 4.5:1 on the raised surface
    reference: neutral[500],
  },
  shadowRgb: '0 0 0', // warmth does not survive near-black, as the base dark theme also found
};

/**
 * Deliberately **not** in `shopThemes`: an owner cannot pick it, the server never stores it, and
 * it carries no shop's identity. It is painted by `clearShopTheme` and by the pre-paint script in
 * each app's `index.html`, and by nothing else.
 */
export const neutralTheme: NeutralTheme = {
  code: NEUTRAL_THEME_CODE,
  defaultColorScheme: 'light',
  label: 'Neutral',
  description: 'No shop. What the app wears before anyone signs in.',
  logoMark: null,
  ramp: neutral,
  palette: { light: NEUTRAL_LIGHT, dark: NEUTRAL_DARK },
};

/**
 * Ordered as the picker shows them, default first. The order is stable because an owner
 * re-opening the form should find the same look in the same place.
 */
export const shopThemes: readonly ShopTheme[] = [
  {
    code: 'oritech',
    defaultColorScheme: 'light',
    label: 'Moss',
    description: "The product's own look — deep green and cream.",
    logoMark: logo.mark,
    ramp: moss,
  },
  {
    code: 'sapphire',
    defaultColorScheme: 'light',
    label: 'Sapphire',
    description: 'A clean, saturated blue.',
    logoMark: null,
    ramp: sapphire,
  },
  {
    code: 'plum',
    defaultColorScheme: 'light',
    label: 'Plum',
    description: 'Deep purple, warm under lamplight.',
    logoMark: null,
    ramp: plum,
  },
  {
    code: 'copper',
    defaultColorScheme: 'light',
    label: 'Copper',
    description: 'Warm bronze, the same family as the buttons.',
    logoMark: null,
    ramp: copper,
  },
  {
    code: 'slate',
    defaultColorScheme: 'light',
    label: 'Slate',
    description: 'Quiet grey-blue, for no colour opinion at all.',
    logoMark: null,
    ramp: slate,
  },
  {
    code: 'diamond',
    defaultColorScheme: 'dark',
    label: 'Diamond',
    description: 'Dark ground, cyan accent. Designed for dark; light if you insist.',
    logoMark: null,
    ramp: diamondCyan,
    palette: { light: DIAMOND_LIGHT, dark: DIAMOND_DARK },
  },
];

export const DEFAULT_SHOP_THEME: ShopThemeCode = 'oritech';

/**
 * Tolerant lookup. An unknown code — a shop themed by a newer deploy, or a hand-edited row —
 * lands on the default rather than on an unstyled page, which is the failure mode that matters:
 * a shop showing the wrong green is a cosmetic bug, a shop showing no colours is a broken app.
 */
export function shopThemeFor(code: string | null | undefined): ShopTheme {
  const found = shopThemes.find((theme) => theme.code === code);
  return found ?? shopThemes[0]!;
}

/**
 * The same lookup, with the neutral look reachable. For code that **paints** rather than picks.
 *
 * `data-shop-theme` reads `neutral` on every signed-out screen, and `shopThemeFor` would resolve
 * that to the default — which is OriTech, and is precisely the leak the neutral look exists to
 * close. MUI's palette is rebuilt from this: it cannot read a CSS variable, so a page whose
 * stylesheet had gone grey would otherwise keep moss buttons, tabs and chips.
 */
export function themeLookFor(code: string | null | undefined): ThemeLook {
  return code === NEUTRAL_THEME_CODE ? neutralTheme : shopThemeFor(code);
}

/**
 * The base theme's shadow recipe over a different colour. The alphas and offsets are
 * `elevation.ts`'s own — only the colour changes, so a Diamond dialog lifts exactly as far as an
 * OriTech one, just not in brown.
 */
function shadowsOver(rgb: string, mode: 'light' | 'dark'): Record<string, string> {
  const light = {
    '--pos-shadow-sm': `0 1px 2px rgb(${rgb} / 0.05), 0 2px 6px rgb(${rgb} / 0.04)`,
    '--pos-shadow-md': `0 2px 4px rgb(${rgb} / 0.05), 0 6px 16px rgb(${rgb} / 0.07)`,
    '--pos-shadow-lg': `0 4px 8px rgb(${rgb} / 0.06), 0 12px 28px rgb(${rgb} / 0.09)`,
    '--pos-shadow-xl': `0 8px 16px rgb(${rgb} / 0.07), 0 24px 56px rgb(${rgb} / 0.12)`,
    '--pos-shadow-column': `0 0 0 1px rgb(${rgb} / 0.04), 0 12px 40px rgb(${rgb} / 0.10)`,
    '--pos-shadow-inset': `inset 0 2px 4px rgb(${rgb} / 0.08)`,
  };
  const dark = {
    '--pos-shadow-sm': `0 1px 2px rgb(${rgb} / 0.4), inset 0 1px 0 rgb(255 255 255 / 0.03)`,
    '--pos-shadow-md': `0 2px 6px rgb(${rgb} / 0.45), inset 0 1px 0 rgb(255 255 255 / 0.04)`,
    '--pos-shadow-lg': `0 6px 18px rgb(${rgb} / 0.5), inset 0 1px 0 rgb(255 255 255 / 0.05)`,
    '--pos-shadow-xl': `0 16px 48px rgb(${rgb} / 0.6), inset 0 1px 0 rgb(255 255 255 / 0.06)`,
    '--pos-shadow-column': `0 0 0 1px rgb(255 255 255 / 0.05), 0 16px 48px rgb(${rgb} / 0.55)`,
    '--pos-shadow-inset': `inset 0 2px 6px rgb(${rgb} / 0.35)`,
  };
  const set = mode === 'light' ? light : dark;

  return {
    ...set,
    '--pos-elevation-flat': 'none',
    '--pos-elevation-raised': set['--pos-shadow-md'],
    '--pos-elevation-overlay': set['--pos-shadow-xl'],
  };
}

/**
 * The brand role for one theme, per mode. Mirrors how `themes.ts` picks steps: the accent moves
 * to the light end of its ramp on dark and takes dark text, because a 600 on a dark surface is a
 * hole and a 400 carrying white is a glare.
 */
function brandRole(ramp: Ramp, mode: 'light' | 'dark'): AccentRole {
  return mode === 'light'
    ? {
        base: ramp[600],
        hover: ramp[700],
        active: ramp[800],
        subtle: ramp[100],
        subtleText: ramp[800],
        on: absolute.white,
      }
    : {
        base: ramp[400],
        hover: ramp[300],
        active: ramp[200],
        subtle: ramp[900],
        subtleText: ramp[200],
        on: neutral[900],
      };
}

/**
 * The variables a theme overrides, as `themeVariables` would have emitted them. Returned rather
 * than written so the CSS builder and the contrast checker read the same function — a look that
 * passes the check is the look that ships, with nothing in between to drift.
 *
 * The default theme returns its overrides too, even though they are what `:root` already says.
 * Emitting them costs six duplicate declarations and buys the guarantee that switching *back* to
 * the default from another theme actually reverts — with an implicit default, `[data-shop-theme]`
 * would have to be removed rather than changed, and one caller forgetting that leaves a shop
 * wearing the last shop's colour.
 */
export function shopThemeVariables(theme: ThemeLook, mode: 'light' | 'dark'): Record<string, string> {
  if (theme.palette !== undefined) {
    const p = theme.palette[mode];
    const accent = (name: 'brand' | 'action', role: AccentRole) => ({
      [`--pos-${name}-base`]: role.base,
      [`--pos-${name}-hover`]: role.hover,
      [`--pos-${name}-active`]: role.active,
      [`--pos-${name}-subtle`]: role.subtle,
      [`--pos-${name}-subtle-text`]: role.subtleText,
      [`--pos-${name}-on`]: role.on,
    });
    const shadow = shadowsOver(p.shadowRgb, mode);

    // Every role except the four statuses and the deltas. Those keep their meaning on every
    // shop; everything else that could carry the product's own look is replaced here, so a
    // full-surface theme is a boundary and not a tint.
    return {
      '--pos-surface-base': p.surface.base,
      '--pos-surface-raised': p.surface.raised,
      '--pos-surface-overlay': p.surface.overlay,
      '--pos-surface-sunken': p.surface.sunken,
      '--pos-surface-backdrop': p.surface.backdrop,
      '--pos-surface-inverse': p.surface.inverse,
      '--pos-text-primary': p.text.primary,
      '--pos-text-secondary': p.text.secondary,
      '--pos-text-muted': p.text.muted,
      '--pos-text-disabled': p.text.disabled,
      '--pos-text-inverse': p.text.inverse,
      '--pos-border-subtle': p.border.subtle,
      '--pos-border-default': p.border.default,
      '--pos-border-card': p.border.card,
      '--pos-border-strong': p.border.strong,
      '--pos-border-focus': p.border.focus,
      ...accent('brand', p.brand),
      ...accent('action', p.action),
      '--pos-focus-ring': p.focus.ring,
      '--pos-focus-halo': p.focus.halo,
      '--pos-scrim': p.scrim,
      '--pos-skeleton': p.skeleton,
      '--pos-chart-value': p.chart.value,
      '--pos-chart-count': p.chart.count,
      '--pos-chart-grid': p.chart.grid,
      '--pos-chart-axis': p.chart.axis,
      '--pos-chart-reference': p.chart.reference,
      ...shadow,
      'color-scheme': mode,
    };
  }

  const brand = brandRole(theme.ramp, mode);

  return {
    '--pos-brand-base': brand.base,
    '--pos-brand-hover': brand.hover,
    '--pos-brand-active': brand.active,
    '--pos-brand-subtle': brand.subtle,
    '--pos-brand-subtle-text': brand.subtleText,
    '--pos-brand-on': brand.on,
    // Dark keeps its own near-black surround: tinting the biggest surface on the screen with a
    // 900 makes the column float in a coloured void, and the identity is already carried by the
    // rail and the mark. Light is where the surround is genuinely the shop's colour.
    ...(mode === 'light' ? { '--pos-surface-backdrop': theme.ramp[100] } : {}),
  };
}
